import { Injectable, inject, DestroyRef } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Decides when a navigation should move the page.
 *
 * The router's own `scrollPositionRestoration` and `anchorScrolling` act on every
 * navigation, and they cannot tell a real one from a same-page URL update. Insights
 * makes a lot of the latter: opening an indicator, its metadata or a patient
 * pathway all navigate purely to record what is open, so the reader can share the
 * link. Those were scrolling the page to the card's anchor, or back to the top on
 * closing, while the reader had not gone anywhere.
 *
 * Restoring the position afterwards was unreliable, because the router scrolls from
 * its own listener and the ordering is not ours to decide. So the two router
 * options are off and the rule is applied here instead:
 *
 *   - the path changed  -> a real move, so scroll (to the fragment, or to the top)
 *   - only the query string or fragment changed -> stay exactly where we are
 *
 * Back and forward are left to the browser's own scroll restoration, which the
 * router had been switching off.
 */
@Injectable({ providedIn: 'root' })
export class RouteScrollService {
  private readonly router = inject(Router);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly destroyRef = inject(DestroyRef);

  /** The path of the page in view, so the next navigation can be compared with it. */
  private currentPath: string | null = null;
  /**
   * How the navigation was started. Back and forward are left alone: the browser
   * restores the position itself, and the router is no longer switching that off.
   */
  private trigger: string = 'imperative';
  private started = false;

  public start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.router.events.pipe(
      filter(event => event instanceof NavigationStart || event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.trigger = event.navigationTrigger;
        return;
      }

      const end = event as NavigationEnd;
      const tree = this.router.parseUrl(end.urlAfterRedirects);
      const path = end.urlAfterRedirects.split('?')[0].split('#')[0];
      const movedPage = path !== this.currentPath;
      this.currentPath = path;

      if (!movedPage || this.trigger === 'popstate' || this.trigger === 'hashchange') {
        return;
      }

      if (tree.fragment) {
        this.scrollToAnchor(tree.fragment);
      } else {
        this.viewportScroller.scrollToPosition([0, 0]);
      }
    });
  }

  /**
   * The target of a deep link is often in a view that has not rendered yet, or is
   * waiting on the data behind it, so the anchor is tried again on the next turn.
   */
  private scrollToAnchor(fragment: string): void {
    this.viewportScroller.scrollToAnchor(fragment);
    setTimeout(() => {
      if (document.getElementById(fragment)) {
        this.viewportScroller.scrollToAnchor(fragment);
      }
    });
  }
}
